import { useState } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { IntegrationsTab } from './IntegrationsTab';
import { ApiTokensTab } from './ApiTokensTab';
import { UsageTab } from './UsageTab';
import { SecurityTab } from './SecurityTab';
import { ManagementTab } from './ManagementTab';
import { AiTab } from './AiTab';

const ALL_TABS = [
  // API tokens first: the one tab EVERY member can act on (self-scoped).
  { key: 'tokens', label: 'API tokens', minRole: null },
  { key: 'integrations', label: 'Integrations', minRole: null },
  { key: 'usage', label: 'Usage', minRole: 'admin' },
  { key: 'security', label: 'Security', minRole: 'account_admin' },
  { key: 'ai', label: 'AI analysis', minRole: 'account_admin' },
  // Management access (#16): the tenant's own account admins only. Never through a management grant.
  { key: 'management', label: 'Management access', minRole: 'account_admin' },
] as const;

type TabKey = (typeof ALL_TABS)[number]['key'];

export function SettingsPage() {
  const { hasRole, tenant, tenants } = useTenant();
  // A DIRECT membership of the active tenant: not a management grant, and not a superadmin who entered it.
  const directMember = tenants.some((t) => t.tenantId === tenant?.tenantId && !t.delegated);
  const TABS = ALL_TABS.filter(
    (t) =>
      (t.minRole === null || hasRole(t.minRole)) &&
      // Delegations are managed by the tenant's own account admins only (the API refuses everyone else).
      !(t.key === 'management' && !directMember)
  );
  const [tab, setTab] = useState<TabKey>('tokens');
  // Clamp: a tenant/role switch can drop the selected tab from the
  // visible set — fall back to the first available instead of a blank
  // page.
  const activeTab = TABS.some((t) => t.key === tab) ? tab : TABS[0].key;
  return (
    <div className="page">
      <h1 className="page-title">Settings</h1>
      <div className="tab-bar" role="tablist" aria-label="Settings">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeTab === t.key}
            className={activeTab === t.key ? 'tab tab-active' : 'tab'}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {activeTab === 'integrations' && <IntegrationsTab />}
      {activeTab === 'tokens' && <ApiTokensTab />}
      {activeTab === 'usage' && hasRole('admin') && <UsageTab />}
      {activeTab === 'security' && hasRole('account_admin') && <SecurityTab />}
      {activeTab === 'ai' && hasRole('account_admin') && <AiTab />}
      {activeTab === 'management' && <ManagementTab />}
    </div>
  );
}
